import './instrument'
import mixpanel from 'mixpanel-browser'
import { createRoot } from 'react-dom/client'
import App from './App.tsx'
import './index.css'

mixpanel.init('067084a2721e7df947da32cfc670cfb8', {
  debug: false,
  track_pageview: false,
  persistence: 'localStorage',
  autocapture: false,
  record_sessions_percent: 100,
})

// Suprimir erros de extensões do navegador e imagens do WhatsApp no console
// Interceptar erros de carregamento de imagem globalmente ANTES de serem logados
const originalError = console.error;
const originalWarn = console.warn;

// Interceptar console.error
console.error = (...args: any[]) => {
  const message = args[0]?.toString() || '';
  const stack = args[1]?.stack || '';
  const allArgsString = args.map(arg => String(arg)).join(' ');
  
  // Filtrar erros de extensões do Chrome (content_script.js)
  if (
    message.includes('content_script.js') ||
    message.includes('Cannot read properties of null') ||
    stack.includes('content_script.js') ||
    args.some(arg => typeof arg === 'string' && arg.includes('content_script.js'))
  ) {
    // Suprimir erros de extensões do navegador silenciosamente
    return;
  }
  
  // Filtrar erros 403/404 de imagens do WhatsApp
  if (
    (message.includes('403') || message.includes('404') || message.includes('Forbidden')) &&
    (message.includes('whatsapp.net') || message.includes('pps.whatsapp') || message.includes('whatsapp'))
  ) {
    // Suprimir este erro silenciosamente
    return;
  }
  
  // Filtrar erros 404 de busca de foto de perfil (esperado quando não há foto)
  // Verificar em todos os argumentos e no stack trace
  if (
    (message.includes('404') || allArgsString.includes('404') || stack.includes('404')) &&
    (message.includes('fetchProfilePictureUrl') || 
     allArgsString.includes('fetchProfilePictureUrl') ||
     stack.includes('fetchProfilePictureUrl') ||
     message.includes('useProfilePictures') ||
     stack.includes('useProfilePictures') ||
     message.includes('Erro na API') ||
     message.includes('Not Found') ||
     message.includes('api.fluity.space') ||
     allArgsString.includes('api.fluity.space'))
  ) {
    // Suprimir erros 404 de foto de perfil silenciosamente
    return;
  }
  
  // Filtrar erros 404 de RPC mark_messages_as_read_bulk (RPC pode não existir)
  if (
    (message.includes('404') || allArgsString.includes('404') || stack.includes('404')) &&
    (message.includes('mark_messages_as_read_bulk') || 
     allArgsString.includes('mark_messages_as_read_bulk') ||
     stack.includes('mark_messages_as_read_bulk'))
  ) {
    // Suprimir erros 404 de RPC silenciosamente
    return;
  }
  
  // Filtrar logs de requisições HTTP 404 do navegador
  if (
    (message.includes('POST') || message.includes('GET') || allArgsString.includes('POST') || allArgsString.includes('GET')) &&
    (message.includes('404') || allArgsString.includes('404')) &&
    (message.includes('fetchProfilePictureUrl') || 
     allArgsString.includes('fetchProfilePictureUrl') || 
     message.includes('mark_messages_as_read_bulk') ||
     allArgsString.includes('mark_messages_as_read_bulk') ||
     message.includes('api.fluity.space') ||
     allArgsString.includes('api.fluity.space'))
  ) {
    // Suprimir logs de requisições 404 esperadas
    return;
  }
  
  // Filtrar erros de WebSocket do Supabase (conexão fechada antes de ser estabelecida)
  if (
    message.includes('WebSocket') &&
    (message.includes('is closed before the connection is established') ||
     message.includes('failed: WebSocket') ||
     message.includes('disconnect') ||
     message.includes('removeChannel'))
  ) {
    // Suprimir erros de WebSocket do Supabase
    return;
  }

  // Suprimir log do Event object de erro do SSE (WebChat Lab); o hook trata e usa console.debug
  if (message.includes('SSE Error') && args.some(a => a?.constructor?.name === 'Event')) {
    return;
  }

  // Erros de rede (Failed to fetch) ao buscar chats/mensagens: tratar como aviso para não assustar ao atualizar a página
  const isNetworkFetchError = message.includes('Failed to fetch') || allArgsString.includes('Failed to fetch');
  if (isNetworkFetchError && (message.includes('useInstagramChats') || allArgsString.includes('useInstagramChats') || allArgsString.includes('Erro ao buscar'))) {
    console.warn(...args);
    return;
  }
  
  // Para todos os outros erros, usar o console.error original
  originalError.apply(console, args);
};

// Interceptar console.warn também
console.warn = (...args: any[]) => {
  const message = args[0]?.toString() || '';
  
  // Filtrar avisos de extensões do Chrome
  if (
    message.includes('content_script.js') ||
    args.some(arg => typeof arg === 'string' && arg.includes('content_script.js'))
  ) {
    // Suprimir avisos de extensões do navegador silenciosamente
    return;
  }
  
  // Filtrar avisos relacionados a imagens do WhatsApp
  if (
    (message.includes('403') || message.includes('404') || message.includes('Failed to load')) &&
    (message.includes('whatsapp.net') || message.includes('pps.whatsapp') || message.includes('whatsapp'))
  ) {
    // Suprimir este aviso silenciosamente
    return;
  }
  
  // Filtrar avisos do FullCalendar sobre opções desconhecidas (atributos data-* de ferramentas de desenvolvimento)
  if (
    message.includes('Unknown option') &&
    (message.includes('data-lov-') || message.includes('data-component-'))
  ) {
    // Suprimir avisos do FullCalendar sobre atributos de debug
    return;
  }
  
  // Filtrar avisos de WebSocket do Supabase (conexão fechada antes de ser estabelecida)
  if (
    message.includes('WebSocket') &&
    (message.includes('is closed before the connection is established') ||
     message.includes('failed: WebSocket'))
  ) {
    // Suprimir avisos de WebSocket do Supabase
    return;
  }
  
  // Para todos os outros avisos, usar o console.warn original
  originalWarn.apply(console, args);
};

// Interceptar erros de carregamento de imagem globalmente (captura fase - mais cedo possível)
window.addEventListener('error', (event) => {
  const errorMessage = event.message || '';
  const errorSource = event.filename || '';
  
  // Suprimir erros de extensões do Chrome (content_script.js)
  if (
    errorSource.includes('content_script.js') ||
    errorMessage.includes('content_script.js') ||
    errorMessage.includes('Cannot read properties of null')
  ) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    return false;
  }
  
  // Suprimir erros 403/404 de imagens do WhatsApp
  if (
    event.target instanceof HTMLImageElement &&
    event.target.src &&
    (event.target.src.includes('whatsapp.net') || event.target.src.includes('pps.whatsapp'))
  ) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    return false;
  }
  
  // Suprimir erros 404 de fetchProfilePictureUrl (esperado quando não há foto)
  if (
    errorMessage.includes('404') &&
    (errorSource.includes('useProfilePictures') || errorMessage.includes('fetchProfilePictureUrl'))
  ) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    return false;
  }
}, true);

// Interceptar também na fase de bubbling
window.addEventListener('error', (event) => {
  const errorMessage = event.message || '';
  const errorSource = event.filename || '';
  
  // Suprimir erros de extensões do Chrome
  if (
    errorSource.includes('content_script.js') ||
    errorMessage.includes('content_script.js') ||
    errorMessage.includes('Cannot read properties of null')
  ) {
    event.preventDefault();
    event.stopPropagation();
    return false;
  }
  
  // Suprimir erros de imagens do WhatsApp
  if (
    event.target instanceof HTMLImageElement &&
    event.target.src &&
    (event.target.src.includes('whatsapp.net') || event.target.src.includes('pps.whatsapp'))
  ) {
    event.preventDefault();
    event.stopPropagation();
    return false;
  }
}, false);

// Interceptar também erros não capturados
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason?.toString() || '';
  
  // Suprimir erros de extensões do Chrome
  if (
    reason.includes('content_script.js') ||
    reason.includes('Cannot read properties of null')
  ) {
    event.preventDefault();
    return;
  }
  
  // Suprimir erros de imagens do WhatsApp
  if (
    (reason.includes('403') || reason.includes('404') || reason.includes('Forbidden')) &&
    (reason.includes('whatsapp.net') || reason.includes('pps.whatsapp') || reason.includes('whatsapp'))
  ) {
    event.preventDefault();
  }
});

createRoot(document.getElementById("root")!).render(<App />);
